import React, { useState, useRef } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../firebase';
import { linkPhone } from '../api';

export default function LinkPhoneModal({ onClose, onLinked }) {
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaVerifier = useRef(null);
  const confirmationResult = useRef(null);

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer(t => {
        if (t <= 1) { clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (phone.length < 10) return setError('Please enter a valid 10-digit phone number.');
    setLoading(true);
    setError('');
    try {
      if (recaptchaVerifier.current) {
        try { recaptchaVerifier.current.clear(); } catch {}
      }
      const container = document.getElementById('link-recaptcha-container');
      if (container) container.innerHTML = '';
      recaptchaVerifier.current = new RecaptchaVerifier(auth, 'link-recaptcha-container', { size: 'invisible' });
      const result = await signInWithPhoneNumber(auth, `+91${phone.trim()}`, recaptchaVerifier.current);
      confirmationResult.current = result;
      setStep('otp');
      startResendTimer();
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 6) return setError('Please enter the 6-digit OTP.');
    setLoading(true);
    setError('');
    try {
      const credential = await confirmationResult.current.confirm(otp);
      const idToken = await credential.user.getIdToken();
      await linkPhone(idToken);
      onLinked();
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/invalid-verification-code') {
        setError('Incorrect OTP. Please try again.');
        setOtp('');
      } else {
        setError(err.message || 'Failed to link phone. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div id="link-recaptcha-container" />
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32,
        width: '100%', maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📱</div>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
            Link your Phone Number
          </h2>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
            Add your phone so you can also sign in with OTP. This is optional.
          </p>
        </div>

        {error && (
          <div style={{ background: '#fff1f2', color: '#e11d48', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 16, border: '1px solid #fda4af' }}>
            {error}
          </div>
        )}

        {step === 'phone' && (
          <>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 700, color: '#64748b', pointerEvents: 'none' }}>+91</div>
              <input
                autoFocus
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                placeholder="10-digit mobile number"
                style={{ width: '100%', padding: '11px 14px 11px 44px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontFamily: 'var(--sans)', fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <button
              onClick={handleSendOtp}
              disabled={loading || phone.length < 10}
              style={{ width: '100%', background: phone.length >= 10 ? 'var(--blue)' : '#e2e8f0', color: phone.length >= 10 ? '#fff' : '#94a3b8', padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', cursor: phone.length >= 10 ? 'pointer' : 'default', marginBottom: 10 }}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </>
        )}

        {step === 'otp' && (
          <>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>OTP sent to <strong>+91 {phone}</strong></p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[0,1,2,3,4,5].map(i => (
                <input
                  key={i}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[i] || ''}
                  autoFocus={i === 0}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    const arr = otp.split('');
                    arr[i] = val;
                    setOtp(arr.join('').slice(0, 6));
                    if (val && i < 5) e.target.parentNode.children[i+1]?.focus();
                  }}
                  onKeyDown={e => { if (e.key === 'Backspace' && !otp[i] && i > 0) e.target.parentNode.children[i-1]?.focus(); }}
                  style={{ flex: '1 1 0', minWidth: 0, width: 0, height: 44, textAlign: 'center', fontSize: 18, fontWeight: 800, border: `2px solid ${otp[i] ? 'var(--blue)' : '#e2e8f0'}`, borderRadius: 8, background: '#f8fafc', color: '#0f172a', outline: 'none', boxSizing: 'border-box' }}
                />
              ))}
            </div>
            <button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length < 6}
              style={{ width: '100%', background: otp.length >= 6 ? 'var(--blue)' : '#e2e8f0', color: otp.length >= 6 ? '#fff' : '#94a3b8', padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', cursor: otp.length >= 6 ? 'pointer' : 'default', marginBottom: 10 }}
            >
              {loading ? 'Verifying...' : 'Verify & Link'}
            </button>
            {resendTimer > 0 ? (
              <p style={{ textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>Resend in {resendTimer}s</p>
            ) : (
              <button onClick={() => { setOtp(''); setStep('phone'); }} style={{ width: '100%', background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Resend OTP</button>
            )}
          </>
        )}

        <button
          onClick={onClose}
          style={{ width: '100%', background: 'transparent', color: '#94a3b8', padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1.5px solid #e2e8f0', cursor: 'pointer', marginTop: 8 }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
